// src/components/PriceOffer.js
import React, { useState, useEffect, useCallback } from "react"
import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  message,
  Upload,
  Image as AntImage,
  Typography,
  Spin,
  Select,
  Row,
  Col,
  List,
  InputNumber,
  Divider,
  Space,
  Popconfirm,
} from "antd" // Assuming you use Ant Design

// --- Firebase Imports ---
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore"
import robotoMed from "../../../fonts/Roboto/Roboto-Medium.ttf"
import robotoReg from "../../../fonts/Roboto/Roboto-Regular.ttf"
import robotoIta from "../../../fonts/Roboto/Roboto-MediumItalic.ttf"
import robotoItalicLight from "../../../fonts/Roboto/Roboto-LightItalic.ttf"
import robotoLight from "../../../fonts/Roboto/Roboto-Light.ttf"
import robotoBold from "../../../fonts/Roboto/Roboto-Bold.ttf"

// --- React PDF Imports ---
import {
  Document,
  Page,
  Image as PdfImage,
  View,
  StyleSheet,
  pdf,
  Text,
  Font,
} from "@react-pdf/renderer"
import { db, storage } from "../../../firebase/config" // Adjust path if needed
import axiosInstance from "../../../axios/axiosConfig"
import { CalculatorOutlined, DeleteOutlined, PlusOutlined, TagsOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons"
import { handleCalculate } from "../../Calculation/handleCalculate"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"

const { Text: AntText, Title, Link } = Typography
const { Option } = Select

// --- Font Registration ---
Font.register({
  family: "Roboto",
  fonts: [
    { src: robotoLight, fontWeight: 300, fontStyle: "normal" },
    { src: robotoReg, fontWeight: "normal", fontStyle: "normal" },
    { src: robotoMed, fontWeight: 500, fontStyle: "normal" },
    { src: robotoItalicLight, fontWeight: 300, fontStyle: "italic" },
    { src: robotoIta, fontWeight: 500, fontStyle: "italic" },
    { src: robotoBold, fontWeight: 700, fontStyle: "normal" },
  ],
})

// ======================================================
// STEP 1: Translation Dictionary
// ======================================================
const translations = {
  en: {
    // Drawer UI
    validationContactName: "Please input the contact name!",
    validationContactEmail: "Please input the contact email!",
    validationValidEmail: "Please enter a valid email!",
    validationLabelName: "Please input the Label Name!",
    validationErrorMsg: "Please fill in all required fields correctly.",
    createPdfButton: "Create Price Offer PDF",
    // PDF Content
    logoMissing: "Logo Missing",
    companyInfo: (name, vat) => `${name || "Company N/A"} | VAT: ${vat || "N/A"}`,
    contactInfo: (phone, email) => {
      const phonePart = phone || translations.en.phoneNA
      const emailPart = email || translations.en.emailNA
      if (phone && email) return `${phonePart} | ${emailPart}`
      if (phone) return phonePart
      if (email) return emailPart
      return `${translations.en.phoneNA} | ${translations.en.emailNA}` // Should not happen if logic holds
    },
    phoneNA: "Phone N/A",
    emailNA: "Email N/A",
    clientInfoHeader: "Client's Information",
    clientNameLabel: "Name:",
    clientCompanyLabel: "Company:",
    clientEmailLabel: "Email:",
    proposalTitle: "Printing Services Proposal",
    quotationIdLabel: "Quotation id:",
    quotationDateLabel: "Quotation Date:",
    productTitle: "Product",
    labelsLabel: "Labels:",
    sizeLabel: "Size:",
    materialLabel: "Material:",
    printTitle: "Print",
    pantoneLabel: "Pantone:",
    cmykLabel: "CMYK:",
    varnishLabel: "Varnish:",
    goldSilverLabel: "Gold/Silver:",
    foilCountLabel: "Foil Count:",
    coveringWhiteLabel: "Covering White:",
    reliefLabel: "Relief:",
    reliefValueYes: "Yes",
    foilColorLabel: "Foil Color:",
    noProductImage: "No Product Image Available",
    pricesTitle: "Prices",
    tableHeaderQty: "Quantity",
    tableHeaderWeight: "Weight (kg)",
    tableHeaderPricePer1000: "Price per 1,000 pcs (EUR)",
    tableHeaderTotal: "Total Cost (EUR)",
    noPrices: "No pricing information available.",
    unitPcs: "pcs",
    unitKg: "kg",
    unitEur: "EUR",
    unitMm: "mm",
    startupFeesTitle: "Startup fees",
    plateDescArsoma: (count) =>
      `Printing Plates (${count} ${translations.en.unitPcs} x 30 ${translations.en.unitEur})`,
    plateDescViva: "Printing Plates/Tooling",
    plateDescDefault: "Printing Plates",
    dieCutLabel: (dims) => `Die Cut ${dims}:`,
    stockAvailableLabel: (dims) => `Stock Available ${dims}:`,
    stockValueFree: "Free",
    totalLabel: "Total:",
    termsTitle: "Pricing & Terms",
    termCompletion: (days) => `Completion: ${days} Working Days`,
    paymentTermsLabel: "Payment Terms:", // The label part
    paymentTermPrePayment: "Pre Payment", // Value for Pre Payment
    paymentTermNetDays: (days) => `Net ${days} Days`, // Function for Net X Days
    termPriceBase:
      "Price: EXW, VAT 0% (Intra-Community Supply, Article 138(1) of the EU VAT Directive)",
    termValidity: "Price valid 1 year",
    termPriceConditions:
      "Prices are in EUR/EXW, based on customer specifications, and may change if printing specs or materials are modified. Quotes are valid for 30 days unless a long-term agreement exists.",
    termToolingFirstOrder:
      "Printing tools/dies are charged once on the first order. Prices marked (*) are tentative until final artwork approval.",
    termToolingChanges: "Any artwork/content changes require new tools/dies, charged accordingly.",
    termToolingRepeat:
      "Storage, refurbishment, and replacement of worn-out tools for repeat orders are covered by the producer at no extra cost.",
    termToolingDisposal:
      "Tools for artworks unused for over a year are either disposed of or returned upon request.",
    termCustomerSupplied:
      "To ensure quality, the producer does not use customer-supplied tools/materials.",
    termMaterialTesting:
      "Specified materials meet customer requirements, but testing is recommended for first-time use to ensure compatibility with the labeling process.",
    termLiability:
      "The producer is not liable for damages occurring after label delivery and acceptance.",
    termAgreement:
      "Placing an order confirms agreement with these terms and full responsibility for chosen materials and printing methods.",
    notAvailable: "N/A", // General N/A
    errorPrefix: "Error:",
    warningPrefix: "Warning:",
    fetchClientError: (id) => `Client with ID ${id} not found.`,
    fetchManufError: (id) => `Manufacturer with ID ${id} not found.`,
    missingManufIdError: "Manufacturer ID not found on the client document.",
    missingManufLogoWarn: "Cropped Logo URL not found on the manufacturer document.",
    sequenceDocError: "Quotation sequence document not found!",
    sequenceLastError: "Invalid 'lastNumber' in sequence document. Must be a number.",
    sequencePrefixError: "Invalid 'prefix' in sequence document. Must be a string.",
    sequenceGenError: "Failed to generate Quotation ID from sequence.",
    invalidKnifeDimsError: "Invalid knife dimensions provided for stock search.",
    stockApiError: "Failed to fetch stock knife data:",
    stockNoMatch: "No suitable stock knife found.",
    unknownMachineTypeWarn: (type) => `Unknown machine type "${type}" for stock knife filtering.`,
    pdfGenError: "An error occurred during PDF generation process:",
    missingInitialDataError: "Missing initial data or Client ID.",
    fileReaderError: "Failed to read image file.",
    imageTypeError: "You can only upload JPG/PNG file!",
    imageSizeError: "Image must smaller than 2MB!",
    //print plates
    plateTypeCMYK: "CMYK",
    plateTypePantone: "Pantone",
    plateTypeCoveringWhite: "Covering White",
    plateTypeVarnish: "Varnish",
    plateTypeGoldSilver: "Gold/Silver",
    plateTypeRelief: "Relief",
    plateTypeFoil: "Foil",
    // Drawer UI (Keep English) - Add labels for new fields
    drawerPaymentTypeLabel: "Payment Type",
    drawerPaymentDaysLabel: "Payment Days (if After Payment)",
    drawerPaymentTypePlaceholder: "Select Type",
    drawerPaymentTypePre: "Pre Payment",
    drawerPaymentTypeAfter: "After Payment",
    validationPaymentType: "Please select a payment type!",
    validationPaymentDays: "Please enter payment days (must be > 0)!",
    drawerCompletionDaysLabel: "Completion Time (Working Days)",
    validationCompletionDays: "Please enter completion days (must be > 0)!",
  },
  lv: {
    // Drawer UI
    validationContactName: "Lūdzu, ievadiet kontaktpersonas vārdu!",
    validationContactEmail: "Lūdzu, ievadiet kontaktpersonas e-pastu!",
    validationValidEmail: "Lūdzu, ievadiet derīgu e-pasta adresi!",
    validationLabelName: "Lūdzu, ievadiet etiķetes nosaukumu!",
    validationErrorMsg: "Lūdzu, aizpildiet visus obligātos laukus pareizi.",
    createPdfButton: "Izveidot cenu piedāvājumu PDF",
    // PDF Content
    logoMissing: "Trūkst logo",
    companyInfo: (name, vat) => `${name || "Uzņēmums N/A"} | PVN: ${vat || "N/A"}`,
    contactInfo: (phone, email) => {
      const phonePart = phone || translations.lv.phoneNA
      const emailPart = email || translations.lv.emailNA
      if (phone && email) return `${phonePart} | ${emailPart}`
      if (phone) return phonePart
      if (email) return emailPart
      return `${translations.lv.phoneNA} | ${translations.lv.emailNA}`
    },
    phoneNA: "Tālrunis N/A",
    emailNA: "E-pasts N/A",
    clientInfoHeader: "Klienta informācija",
    clientNameLabel: "Vārds:",
    clientCompanyLabel: "Uzņēmums:",
    clientEmailLabel: "E-pasts:",
    proposalTitle: "Poligrāfijas pakalpojumu piedāvājums",
    quotationIdLabel: "Piedāvājuma ID:",
    quotationDateLabel: "Piedāvājuma datums:",
    productTitle: "Produkts",
    labelsLabel: "Etiķetes:",
    sizeLabel: "Izmērs:",
    materialLabel: "Materiāls:",
    printTitle: "Druka",
    pantoneLabel: "Pantone:",
    cmykLabel: "CMYK:",
    varnishLabel: "Laka:",
    goldSilverLabel: "Zelts/Sudrabs:",
    foilCountLabel: "Foliju skaits:",
    coveringWhiteLabel: "Sedzoši balts:",
    reliefLabel: "Reljefs:",
    reliefValueYes: "Jā",
    foilColorLabel: "Folijas krāsa:",
    noProductImage: "Produkta attēls nav pieejams",
    pricesTitle: "Cenas",
    tableHeaderQty: "Daudzums",
    tableHeaderWeight: "Svars (kg)",
    tableHeaderPricePer1000: "Cena par 1000 gab. (EUR)",
    tableHeaderTotal: "Kopējā summa (EUR)",
    noPrices: "Informācija par cenām nav pieejama.",
    unitPcs: "gab.",
    unitKg: "kg",
    unitEur: "EUR",
    unitMm: "mm",
    startupFeesTitle: "Sākuma izmaksas",
    plateDescArsoma: (count) =>
      `Drukas plates (${count} ${translations.lv.unitPcs} x 30 ${translations.lv.unitEur})`,
    plateDescViva: "Drukas plates/Instrumenti",
    plateDescDefault: "Drukas plates",
    dieCutLabel: (dims) => `Nazis ${dims}:`,
    stockAvailableLabel: (dims) => `Noliktavā pieejams ${dims}:`,
    stockValueFree: "Bezmaksas",
    totalLabel: "Kopā:",
    termsTitle: "Cenas un noteikumi",
    termCompletion: (days) => `Izpilde: ${days} darba dienas`,
    paymentTermsLabel: "Apmaksas noteikumi:",
    paymentTermPrePayment: "Priekšapmaksa",
    paymentTermNetDays: (days) => `Neto ${days} dienas`,
    termPriceBase:
      "Cena: EXW, PVN 0% (ES piegāde Kopienas iekšienē, PVN Direktīvas 138. panta 1. punkts)",
    termValidity: "Cena spēkā 1 gadu",
    termPriceConditions:
      "Cenas norādītas EUR/EXW, pamatojoties uz klienta specifikācijām, un var mainīties, ja tiek mainītas drukas specifikācijas vai materiāli. Piedāvājumi ir spēkā 30 dienas, ja vien nepastāv ilgtermiņa līgums.",
    termToolingFirstOrder:
      "Drukas instrumenti/naži tiek iekasēti vienreiz pirmajā pasūtījumā. Cenas, kas atzīmētas ar (*), ir provizoriskas līdz galīgā maketa apstiprināšanai.",
    termToolingChanges:
      "Jebkuras maketa/satura izmaiņas prasa jaunus instrumentus/nažus, par kuriem attiecīgi tiek iekasēta maksa.",
    termToolingRepeat:
      "Nolietoto instrumentu uzglabāšanu, atjaunošanu un nomaiņu atkārtotiem pasūtījumiem sedz ražotājs bez papildu maksas.",
    termToolingDisposal:
      "Instrumenti maketiem, kas nav izmantoti ilgāk par gadu, tiek vai nu utilizēti, vai atdoti pēc pieprasījuma.",
    termCustomerSupplied:
      "Lai nodrošinātu kvalitāti, ražotājs neizmanto klienta piegādātos instrumentus/materiālus.",
    termMaterialTesting:
      "Norādītie materiāli atbilst klienta prasībām, taču pirmajā lietošanas reizē ieteicama testēšana, lai nodrošinātu saderību ar etiķetēšanas procesu.",
    termLiability:
      "Ražotājs nav atbildīgs par bojājumiem, kas radušies pēc etiķešu piegādes un pieņemšanas.",
    termAgreement:
      "Pasūtījuma veikšana apstiprina piekrišanu šiem noteikumiem un pilnu atbildību par izvēlētajiem materiāliem un drukas metodēm.",
    notAvailable: "N/A",
    errorPrefix: "Kļūda:",
    warningPrefix: "Brīdinājums:",
    fetchClientError: (id) => `Klients ar ID ${id} nav atrasts.`,
    fetchManufError: (id) => `Ražotājs ar ID ${id} nav atrasts.`,
    missingManufIdError: "Klienta dokumentā nav atrasts ražotāja ID.",
    missingManufLogoWarn: "Ražotāja dokumentā nav atrasts apgriezts logo URL.",
    sequenceDocError: "Cenu piedāvājumu secības dokuments nav atrasts!",
    sequenceLastError: "Nederīgs 'lastNumber' secības dokumentā. Jābūt skaitlim.",
    sequencePrefixError: "Nederīgs 'prefix' secības dokumentā. Jābūt virknei.",
    sequenceGenError: "Neizdevās ģenerēt piedāvājuma ID no secības.",
    invalidKnifeDimsError: "Nodrošināti nederīgi naža izmēri krājumu meklēšanai.",
    stockApiError: "Neizdevās ielādēt noliktavas nažu datus:",
    stockNoMatch: "Piemērots nazis noliktavā nav atrasts.",
    unknownMachineTypeWarn: (type) =>
      `Nezināms iekārtas tips "${type}" noliktavas nažu filtrēšanai.`,
    pdfGenError: "Kļūda PDF ģenerēšanas procesā:",
    missingInitialDataError: "Trūkst sākotnējo datu vai klienta ID.",
    fileReaderError: "Neizdevās nolasīt attēla failu.",
    imageTypeError: "Var augšupielādēt tikai JPG/PNG failus!",
    imageSizeError: "Attēlam jābūt mazākam par 2MB!",
    //print
    plateTypeCMYK: "CMYK",
    plateTypePantone: "Pantone",
    plateTypeCoveringWhite: "Sedzoši balts",
    plateTypeVarnish: "Laka",
    plateTypeGoldSilver: "Zelts/Sudrabs",
    plateTypeRelief: "Reljefs", // Covers Arsoma Relief & Viva Embossing Tool
    plateTypeFoil: "Folija",
  },
  ru: {
    // Drawer UI
    drawerTitle: "Настроить детали PDF",
    drawerLangLabel: "Язык документа",
    drawerContactNameLabel: "Контактное лицо клиента",
    drawerContactEmailLabel: "Эл. почта контактного лица",
    drawerProductLabelNameLabel: "Название этикетки продукта (для PDF)",
    drawerProductImageLabel: "Изображение продукта (для PDF)",
    drawerRemoveImage: "Удалить изображение",
    drawerChangeImage: "Изменить изображение",
    drawerUploadImage: "Загрузить изображение",
    drawerImageSizeInfo: "Макс. 2МБ (JPG/PNG)",
    drawerCancel: "Отмена",
    drawerConfirm: "Подтвердить и сгенерировать PDF",
    drawerLoading: "Загрузка начальных данных...",
    drawerNoImage: "Изображение не выбрано",
    validationContactName: "Пожалуйста, введите имя контактного лица!",
    validationContactEmail: "Пожалуйста, введите эл. почту контактного лица!",
    validationValidEmail: "Пожалуйста, введите действительный адрес эл. почты!",
    validationLabelName: "Пожалуйста, введите название этикетки!",
    validationErrorMsg: "Пожалуйста, заполните все обязательные поля правильно.",
    createPdfButton: "Создать ценовое предложение PDF",
    // PDF Content
    logoMissing: "Логотип отсутствует",
    companyInfo: (name, vat) => `${name || "Компания N/A"} | VAT: ${vat || "N/A"}`,
    contactInfo: (phone, email) => {
      const phonePart = phone || translations.ru.phoneNA
      const emailPart = email || translations.ru.emailNA
      if (phone && email) return `${phonePart} | ${emailPart}`
      if (phone) return phonePart
      if (email) return emailPart
      return `${translations.ru.phoneNA} | ${translations.ru.emailNA}`
    },
    phoneNA: "Телефон N/A",
    emailNA: "Эл. почта N/A",
    clientInfoHeader: "Информация о клиенте",
    clientNameLabel: "Имя:",
    clientCompanyLabel: "Компания:",
    clientEmailLabel: "Эл. почта:",
    proposalTitle: "Предложение полиграфических услуг",
    quotationIdLabel: "ID предложения:",
    quotationDateLabel: "Дата предложения:",
    productTitle: "Продукт",
    labelsLabel: "Этикетки:",
    sizeLabel: "Размер:",
    materialLabel: "Материал:",
    printTitle: "Печать",
    pantoneLabel: "Pantone:",
    cmykLabel: "CMYK:",
    varnishLabel: "Лак:",
    goldSilverLabel: "Золото/Серебро:",
    foilCountLabel: "Кол-во фольги:",
    coveringWhiteLabel: "Кроющий белый:",
    reliefLabel: "Рельеф:",
    reliefValueYes: "Да",
    foilColorLabel: "Цвет фольги:",
    noProductImage: "Изображение продукта недоступно",
    pricesTitle: "Цены",
    tableHeaderQty: "Количество",
    tableHeaderWeight: "Вес (кг)",
    tableHeaderPricePer1000: "Цена за 1000 шт. (EUR)",
    tableHeaderTotal: "Общая стоимость (EUR)",
    noPrices: "Информация о ценах недоступна.",
    unitPcs: "шт.",
    unitKg: "кг",
    unitEur: "EUR",
    unitMm: "мм",
    startupFeesTitle: "Начальные затраты",
    plateDescArsoma: (count) =>
      `Печатные формы (${count} ${translations.ru.unitPcs} x 30 ${translations.ru.unitEur})`,
    plateDescViva: "Печатные формы/Инструменты",
    plateDescDefault: "Печатные формы",
    dieCutLabel: (dims) => `Высекальный нож ${dims}:`,
    stockAvailableLabel: (dims) => `Доступно на складе ${dims}:`,
    stockValueFree: "Бесплатно",
    totalLabel: "Итого:",
    termsTitle: "Цены и условия",
    termCompletion: (days) => `Срок выполнения: ${days} рабочих дней`,
    paymentTermsLabel: "Условия оплаты:",
    paymentTermPrePayment: "Предоплата",
    paymentTermNetDays: (days) => `Нетто ${days} дней`,
    termPriceBase: "Цена: EXW, НДС 0% (Внутрисоюзная поставка, Статья 138(1) Директивы ЕС по НДС)",
    termValidity: "Цена действительна 1 год",
    termPriceConditions:
      "Цены указаны в EUR/EXW, основаны на спецификациях клиента и могут изменяться при изменении спецификаций печати или материалов. Предложения действительны 30 дней, если не существует долгосрочного соглашения.",
    termToolingFirstOrder:
      "Плата за печатные инструменты/штампы взимается один раз при первом заказе. Цены, отмеченные (*), являются предварительными до окончательного утверждения макета.",
    termToolingChanges:
      "Любые изменения макета/содержимого требуют новых инструментов/штампов, плата за которые взимается соответствующим образом.",
    termToolingRepeat:
      "Хранение, восстановление и замена изношенных инструментов для повторных заказов покрываются производителем без дополнительной платы.",
    termToolingDisposal:
      "Инструменты для макетов, не использовавшихся более года, либо утилизируются, либо возвращаются по запросу.",
    termCustomerSupplied:
      "Для обеспечения качества производитель не использует инструменты/материалы, предоставленные заказчиком.",
    termMaterialTesting:
      "Указанные материалы соответствуют требованиям заказчика, но рекомендуется тестирование при первом использовании для обеспечения совместимости с процессом этикетирования.",
    termLiability:
      "Производитель не несет ответственности за ущерб, возникший после доставки и приемки этикеток.",
    termAgreement:
      "Размещение заказа подтверждает согласие с настоящими условиями и полную ответственность за выбранные материалы и методы печати.",
    notAvailable: "Н/Д", // Not Available
    errorPrefix: "Ошибка:",
    warningPrefix: "Предупреждение:",
    fetchClientError: (id) => `Клиент с ID ${id} не найден.`,
    fetchManufError: (id) => `Производитель с ID ${id} не найден.`,
    missingManufIdError: "ID производителя не найден в документе клиента.",
    missingManufLogoWarn: "URL обрезанного логотипа не найден в документе производителя.",
    sequenceDocError: "Документ последовательности ценовых предложений не найден!",
    sequenceLastError:
      "Недопустимое значение 'lastNumber' в документе последовательности. Должно быть числом.",
    sequencePrefixError:
      "Недопустимое значение 'prefix' в документе последовательности. Должна быть строка.",
    sequenceGenError: "Не удалось сгенерировать ID предложения из последовательности.",
    invalidKnifeDimsError: "Предоставлены недопустимые размеры ножа для поиска на складе.",
    stockApiError: "Не удалось загрузить данные о ножах со склада:",
    stockNoMatch: "Подходящий нож на складе не найден.",
    unknownMachineTypeWarn: (type) =>
      `Неизвестный тип машины "${type}" для фильтрации ножей на складе.`,
    pdfGenError: "Произошла ошибка в процессе генерации PDF:",
    missingInitialDataError: "Отсутствуют начальные данные или ID клиента.",
    fileReaderError: "Не удалось прочитать файл изображения.",
    imageTypeError: "Можно загружать только файлы JPG/PNG!",
    imageSizeError: "Изображение должно быть меньше 2МБ!",
    //print
    plateTypeCMYK: "CMYK",
    plateTypePantone: "Pantone",
    plateTypeCoveringWhite: "Кроющий белый",
    plateTypeVarnish: "Лак",
    plateTypeGoldSilver: "Золото/Серебро",
    plateTypeRelief: "Рельеф", // Covers Arsoma Relief & Viva Embossing Tool
    plateTypeFoil: "Фольга",
  },
}

// ======================================================
// Helper to get translation based on selected language
// Defaults to English if language or key is missing
// ======================================================
const getTranslation = (lang, key, ...args) => {
  const langDict = translations[lang] || translations.en
  const translationValue = langDict[key] || translations.en[key]

  if (typeof translationValue === "function") {
    return translationValue(...args)
  }
  return translationValue || `MISSING_TRANSLATION[${key}]` // Fallback for missing keys
}

function calculatePlateInfo(initialData, lang) {
  let plateCost = 0
  const breakdown = [] // <<< NEW: Array to hold breakdown details
  // No longer need descriptionKey or plateCount here

  // const t = (key, ...args) => getTranslation(lang, key, ...args) // Local t function

  const print = initialData?.print
  const machine = initialData?.machine

  if (print && machine === "Arsoma") {
    const platePrice = 30 // Arsoma cost per plate

    // Add entries for each plate type based on count
    for (let i = 0; i < (print.cmykColors || 0); i++) {
      breakdown.push({ key: "plateTypeCMYK", cost: platePrice })
      plateCost += platePrice
    }
    for (let i = 0; i < (print.pantoneColors || 0); i++) {
      breakdown.push({ key: "plateTypePantone", cost: platePrice })
      plateCost += platePrice
    }
    for (let i = 0; i < (print.coveringWCount || 0); i++) {
      breakdown.push({ key: "plateTypeCoveringWhite", cost: platePrice })
      plateCost += platePrice
    }
    if ((print.varnishUse || 0) > 0) {
      breakdown.push({ key: "plateTypeVarnish", cost: platePrice })
      plateCost += platePrice
    }
    if (print.goldOrSilver > 0) {
      // Assuming goldOrSilver holds the count
      for (let i = 0; i < print.goldOrSilver; i++) {
        breakdown.push({ key: "plateTypeGoldSilver", cost: platePrice })
        plateCost += platePrice
      }
    }
    if (print.relief && print.relief !== "No") {
      breakdown.push({ key: "plateTypeRelief", cost: platePrice })
      plateCost += platePrice
    }
  } else if (print && machine === "Viva") {
    const flexoPlatePrice = 30
    const foilPlatePrice = 100
    const reliefPlatePrice = 150 // Assuming this is Embossing/Relief tool

    // Add entries for each specific tool/plate type for Viva
    for (let i = 0; i < (print.coveringWCount || 0); i++) {
      breakdown.push({ key: "plateTypeCoveringWhite", cost: flexoPlatePrice })
      plateCost += flexoPlatePrice
    }
    if ((print.varnishUse || 0) > 0) {
      breakdown.push({ key: "plateTypeVarnish", cost: flexoPlatePrice })
      plateCost += flexoPlatePrice
    }
    if (print.goldOrSilver > 0) {
      for (let i = 0; i < print.goldOrSilver; i++) {
        breakdown.push({ key: "plateTypeGoldSilver", cost: flexoPlatePrice })
        plateCost += flexoPlatePrice
      }
    }
    for (let i = 0; i < (print.foilCount || 0); i++) {
      breakdown.push({ key: "plateTypeFoil", cost: foilPlatePrice })
      plateCost += foilPlatePrice
    }
    if (print.relief && print.relief !== "No") {
      // Using 'plateTypeRelief' key, but cost is specific to Viva's tool
      breakdown.push({ key: "plateTypeRelief", cost: reliefPlatePrice })
      plateCost += reliefPlatePrice
    }
  }

  // Return total cost and the breakdown array
  return { plateCost, breakdown }
}

// --- Styles for the PDF ---
const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 30,
    backgroundColor: "#FFFFFF",
    fontFamily: "Roboto",
    fontSize: 9,
  },
  topSectionContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start", // Align tops of children initially
    marginBottom: 0,
    width: "100%",
  },

  mainContentContainer: {
    flexDirection: "row", // Arrange children (columns) horizontally
    width: "100%", // Take full page width
    paddingTop: 20, // Add space below the header (adjust as needed)
  },
  leftColumn: {
    width: "60%", // Left column takes 60% of the width
    paddingRight: 10, // Add some space between left and right columns
    //border: '1px solid red', // Optional: for debugging layout
  },
  rightColumn: {
    width: "40%", // Right column takes the remaining 40%
    paddingLeft: 10, // Add some space between left and right columns
    //border: '1px solid blue', // Optional: for debugging layout
    // We will add content here later
  },

  headerContainer: {
    // Left side
    alignItems: "flex-start",
  },
  clientInfoContainer: {
    // Right side
    alignItems: "flex-end",
    // ***** ADDED marginTop to push down *****
    // Value should approx match logo height + logo margin-bottom
    // Adjust this value if logo size/margin changes!
    marginTop: 83, //  (75 height + 8 margin)
  },
  logo: {
    width: 100,
    height: 75, // Height used for marginTop calculation
    objectFit: "contain",
    marginBottom: 8, // Margin used for marginTop calculation
  },
  companyInfoLine: {
    fontSize: 9,
    fontWeight: 500,
    color: "#333333",
    marginBottom: 4,
    textAlign: "left",
  },
  contactInfoLine: {
    fontSize: 9,
    color: "#595959",
    fontWeight: 300,
    textAlign: "left",
  },
  clientInfoHeader: {
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "Roboto",
    marginBottom: 3,
    textAlign: "right",
  },
  underline: {
    width: "100%",
    height: 0.5,
    backgroundColor: "#BDBDBD",
    marginBottom: 3,
    alignSelf: "flex-end",
  },
  clientInfoDetail: {
    fontSize: 9,
    marginBottom: 3,
    textAlign: "right",
    color: "#333333",
    fontWeight: 300,
    fontFamily: "Roboto",
  },
  titleSectionContainer: {
    marginTop: 5, // Add space below the header section
    marginBottom: 5, // Add space before the main content starts
    alignItems: "start", // Center the main title horizontally
    width: "100%",
  },
  mainTitle: {
    fontSize: 14, // Make title larger
    fontWeight: 500, // Bold
    fontFamily: "Roboto",
    marginBottom: 4, // Space between title and details
    textAlign: "left", // Explicitly center text
    color: "#1A1A1A", // Darker color for title
  },
  detailsContainer: {
    width: "100%", // Take full width to allow left alignment within it
    alignItems: "flex-start", // Align detail items to the left
    // Removed marginTop here, spacing handled by mainTitle marginBottom
  },
  dividerLine: {
    width: "70%", // Set width to 3/4 of the container (which is page width)
    height: 0.5, // Make it very thin (0.5 pt)
    backgroundColor: "#BDBDBD", // Light grey color, adjust as needed (e.g., '#CCCCCC', '#BDBDBD')
    marginTop: 2, // Space above the line (after the title)
    marginBottom: 4, // Space below the line (before the details)
  },
  detailText: {
    fontSize: 9,
    fontFamily: "Roboto",
    fontWeight: 300, // Lighter weight for details
    marginBottom: 3, // Space between detail lines
    textAlign: "left", // Explicitly left-align text
    color: "#333333",
  },
  detailLine: {
    // A view to hold label + value on one line
    flexDirection: "row", // Arrange children horizontally
    marginBottom: 3, // Space between lines (ID line and Date line)
  },
  detailTextLabel: {
    // Style for the "Label: " part
    fontSize: 9,
    fontFamily: "Roboto",
    fontWeight: 300, // Regular/Light weight
    color: "#333333",
    // No marginBottom needed here, handled by detailLine
  },
  detailTextValue: {
    // Style for the dynamic value part
    fontSize: 9,
    fontFamily: "Roboto",
    fontWeight: 700, // Bold weight
    color: "#1A1A1A", // Slightly darker/bolder color maybe
    // No marginBottom needed here
  },
  productSectionContainer: {
    marginTop: 15, // Add space after the quotation details section
    marginBottom: 5,
    width: "100%", // Takes full width of the left column
    alignItems: "flex-start", // Align content (title, details) to the left
  },
  printSectionContainer: {
    marginTop: 15, // Add space after the product section
    marginBottom: 5,
    width: "100%", // Takes full width of the left column
    alignItems: "flex-start", // Align content (title, details) to the left
  },
  printDetailsGridContainer: {
    flexDirection: "row", // Arrange items horizontally
    flexWrap: "wrap", // Allow items to wrap to the next line
    width: "100%", // Take full width of its container
    // marginTop: 5, // Optional space below the "Print" title
  },
  printDetailItem: {
    width: "50%", // Aim for 3 items per row (adjust if needed)
    flexDirection: "row", // Keep label and value side-by-side within the item
    marginBottom: 5, // Vertical spacing between rows of items
    paddingRight: 5, // Add a small horizontal gap between items
  },
  priceSectionContainer: {
    marginTop: 15, // Space after the previous section
    marginBottom: 5,
    width: "100%",
    alignItems: "flex-start",
  },
  tableContainer: {
    width: "100%",
    marginTop: 4, // Space after the divider
    borderWidth: 0.5, // Add a light border around the table
    borderColor: "#BDBDBD",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#434343",
    borderBottomWidth: 0.5,
    borderBottomColor: "#BDBDBD",
    paddingVertical: 4, // Padding for header row
    paddingHorizontal: 4, // Padding for header row
  },
  tableDataRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#BDBDBD", // Lighter border for data rows
    paddingVertical: 3, // Padding for data rows
    paddingHorizontal: 4, // Padding for data rows
  },
  // --- Column Widths & Alignment ---
  tableColQty: {
    // Style for Quantity column cells (header and data)
    width: "25%",
    textAlign: "center", // Right-align quantity numbers
    paddingRight: 5, // Add padding to separate from next column
  },
  tableColWeight: {
    width: "25%", // Added width
    textAlign: "center",
    paddingRight: 5,
  },
  tableColPricePer1000: {
    // Style for Price/1000 column cells
    width: "25%",
    textAlign: "center",
    paddingRight: 5,
  },
  tableColTotal: {
    // Style for Total Cost column cells
    width: "25%",
    textAlign: "center",
  },
  // --- Specific Header/Data Text Styles ---
  tableHeaderText: {
    fontWeight: 500, // Make header text slightly bolder
    fontSize: 9, // Ensure consistent font size
    color: "#FFFFFF", // <<< ADDED: White color for header text
  },
  tableDataText: {
    fontSize: 9, // Ensure consistent font size
    fontWeight: 300, // Normal weight for data
  },
  noPricesText: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#595959",
    padding: 10,
    textAlign: "center",
  },
  dividerLineFull: {
    width: "100%", // Make divider full width now
    height: 0.5,
    backgroundColor: "#BDBDBD",
    marginTop: 4,
    marginBottom: 10,
  },
  mainTitleFull: {
    fontSize: 14, // Make title larger
    fontWeight: 500, // Bold
    fontFamily: "Roboto",
    marginBottom: 4, // Space between title and details
    textAlign: "center",
    color: "#1A1A1A", // Darker color for title
    width: "100%",
  },
  // --- NEW Styles for Startup Fees Section ---
  startupSectionContainer: {
    marginTop: 20, // Space after the prices table
    marginBottom: 5,
    width: "100%",
    alignItems: "center", // Center title and divider
  },
  costSummaryContainer: {
    width: "100%", // Let summary lines take full width
    marginTop: 0, // Margin handled by dividerLineFull above
    paddingHorizontal: 10, // Indent the summary slightly? Optional.
    // alignItems: 'flex-start', // Default
  },
  summaryLine: {
    flexDirection: "row",
    justifyContent: "space-between", // Push label left, value right
    width: "100%", // Take full width within container
    marginBottom: 4,
    paddingHorizontal: 5, // Padding within the line
  },
  summaryLabel: {
    fontSize: 9,
    fontWeight: 300,
    color: "#333333",
    textAlign: "left",
  },
  summaryValue: {
    fontSize: 9,
    fontWeight: 700, // Bold value
    color: "#1A1A1A",
    textAlign: "right",
  },
  summarySeparator: {
    // Thin line before total
    width: "100%", // Full width separator
    height: 0.5,
    backgroundColor: "#E0E0E0", // Lighter separator
    marginTop: 3,
    marginBottom: 5,
  },
  summaryTotalLabel: {
    fontWeight: 500, // Make total label slightly bolder
  },
  summaryTotalValue: {
    fontWeight: 700, // Keep total value bold (or make 900 if font supports)
    // Maybe add underline?
    textDecoration: "underline", // Underline might not work, depends on renderer version
  },
  // --- NEW Styles for Pricing & Terms Section ---
  termsSectionContainer: {
    marginTop: 20, // Space after the startup fees section (if shown)
    marginBottom: 5,
    width: "100%",
    alignItems: "center", // Center title and divider
  },
  bulletListContainer: {
    width: "100%",
    paddingLeft: 10, // Indent bullet list slightly
    marginBottom: 10,
    alignItems: "flex-start", // Align bullet items to the left
  },
  bulletItem: {
    flexDirection: "row",
    marginBottom: 3,
    width: "100%", // Ensure item takes width for text wrapping
  },
  bulletSymbol: {
    // Using a standard bullet character directly in Text might be simpler
    // Or style this Text component:
    width: 10, // Fixed width for bullet alignment
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    color: "#434343",
  },
  bulletText: {
    flex: 1, // Allow text to take remaining space and wrap
    fontSize: 9,
    fontWeight: 300,
    color: "#333333",
    textAlign: "left",
  },
  // --- NEW Style for Product Image ---
  productImage: {
    maxWidth: "100%", // Don't exceed column width
    maxHeight: 200, // Limit maximum height (adjust as needed)
    objectFit: "contain", // Scale image while preserving aspect ratio
    marginTop: 10, // Add some space at the top if needed
  },
  placeholderText: {
    // Optional style for placeholder
    fontSize: 9,
    color: "#999999",
    marginTop: 20,
    textAlign: "center",
  },
})

// --- The PDF Document Structure (No structural change needed here) ---
const SimpleLogoPdf = ({
  finalDataForPdf, // <<< Receive the combined data object
  manufacturer,
  client,
  initialData, // Still needed for product details, prices etc.
  quotationId,
  stockKnifeDimensions,
  priceOfferTerms, // Firestore terms
}) => {
  const lang = finalDataForPdf?.language || "en"
  const overriddenContactName = finalDataForPdf?.contactName
  const overriddenContactEmail = finalDataForPdf?.contactEmail
  const overriddenProductName = finalDataForPdf?.productLabelName
  const overriddenProductImageUrl = finalDataForPdf?.productImageUrl
  const paymentType = finalDataForPdf?.paymentType
  const paymentDays = finalDataForPdf?.paymentDays
  const completionDays = finalDataForPdf?.completionDays

  // Get translations for the selected language
  const t = (key, ...args) => getTranslation(lang, key, ...args)
  // --- Date Formatting based on Language ---
  const formatDate = (date, lang) => {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" }
    let locale = "en-US" // Default
    if (lang === "lv") locale = "lv-LV"
    if (lang === "ru") locale = "ru-RU"
    return date.toLocaleDateString(locale, options)
  }
  // Get current date and format it (e.g., DD.MM.YYYY)
  const currentDate = new Date()

  // Pad day and month with leading zero if needed
  const formattedDate = formatDate(currentDate, lang)

  // --- Prepare Product Data (with fallbacks using translations) ---
  const productName = overriddenProductName || t("notAvailable")
  const labelWidth = initialData?.labelWidth
  const labelHeight = initialData?.labelHeight
  const material = initialData?.material || t("notAvailable")
  const sizeString =
    labelWidth && labelHeight ? `${labelWidth}x${labelHeight} ${t("unitMm")}` : t("notAvailable")
  const machineType = initialData?.machine
  const printData = initialData?.print

  // --- End Product Data Prep ---

  // --- Prepare Price Data ---
  const fixedPrices = initialData?.dynamicFixedPrice
  const dynamicPrices = initialData?.dynamicPrice
  const pricesToDisplay =
    fixedPrices?.length > 0 ? fixedPrices : dynamicPrices?.length > 0 ? dynamicPrices : []

  // --- Get Product Image URL ---
  const productImageUrl = overriddenProductImageUrl // Use override

  // --- Default completion days if missing from input (safety net) ---
  const finalCompletionDays =
    typeof completionDays === "number" && completionDays > 0 ? completionDays : 10 // Default to 10 if invalid/missing
  if (finalCompletionDays !== completionDays) {
    console.warn(
      `PDF Generation: Invalid completion days provided (${completionDays}). Falling back to ${finalCompletionDays}.`,
    )
  }

  // --- Formatting Helpers based on Language ---
  const getLocale = (lang) => {
    if (lang === "lv") return "lv-LV"
    if (lang === "ru") return "ru-RU"
    return "en-US" // Default to US English for consistency if needed elsewhere
  }
  const currentLocale = getLocale(lang)

  const formatCurrency = (value) => {
    if (typeof value !== "number") return t("notAvailable")
    return value.toLocaleString(currentLocale, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  const formatQuantity = (value) => {
    if (typeof value !== "number") return t("notAvailable")
    return value.toLocaleString(currentLocale)
  }
  const formatWeight = (value) => {
    if (typeof value !== "number") return t("notAvailable")
    return value.toLocaleString(currentLocale, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  // --- *** NEW: Construct Dynamic Payment Term *** ---
  let dynamicPaymentTermString = ""
  if (paymentType === "pre") {
    dynamicPaymentTermString = `${t("paymentTermsLabel")} ${t("paymentTermPrePayment")}`
  } else if (paymentType === "after" && paymentDays && paymentDays > 0) {
    dynamicPaymentTermString = `${t("paymentTermsLabel")} ${t("paymentTermNetDays", paymentDays)}`
  } else {
    // Fallback: If data is somehow missing, default to Net 30 or display N/A
    // Using Net 30 as a safe default fallback for now.
    dynamicPaymentTermString = `${t("paymentTermsLabel")} ${t("paymentTermNetDays", 30)}`
    console.warn(
      `PDF Generation: Payment type/days missing or invalid (Type: ${paymentType}, Days: ${paymentDays}). Falling back to default.`,
    )
  }

  // --- Calculate Startup Costs ---
  const startupData = initialData?.startup
  const showStartupSection = startupData?.newJob === true || startupData?.newKnife === true

  // Initialize plateInfo with expected structure
  let plateInfo = { plateCost: 0, breakdown: [] }
  let showDieCut = false
  let dieCutCost = 0
  let dieCutDims = ""
  let totalStartupCost = 0
  let stockKnifeDimsString = ""
  let showStockAvailableLine = false

  // Calculate only if the section will be shown
  if (showStartupSection) {
    plateInfo = calculatePlateInfo(initialData, lang) // <<< Gets { plateCost, breakdown }
    showDieCut = startupData?.newKnife === true
    if (showDieCut) {
      dieCutCost = startupData.knifePrice || 0
      const dieCutWidth = startupData?.newKnifeObject?.width || "?"
      const dieCutHeight = startupData?.newKnifeObject?.height || "?"
      dieCutDims = `(${dieCutWidth} x ${dieCutHeight} ${t("unitMm")})`
    }
    totalStartupCost = plateInfo.plateCost + dieCutCost
  }

  // --- Helper to format the breakdown string ---
  const formatPlateBreakdown = (breakdownArray) => {
    if (!breakdownArray || breakdownArray.length === 0) {
      return ""
    }
    // Map each item to "TranslatedName (CostEUR)" format
    const formattedItems = breakdownArray.map((item) => {
      const translatedType = t(item.key) // Translate the key (e.g., 'plateTypeCMYK' -> 'CMYK')
      // Format cost directly here for simplicity
      const formattedCost = `${item.cost}${t("unitEur")}` // e.g., "30EUR"
      return `${translatedType} (${formattedCost})` // e.g., "CMYK (30EUR)"
    })
    // Join with " + "
    return formattedItems.join(" + ")
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* --- Top Section Container (Header - Remains Full Width) --- */}
        <View style={styles.topSectionContainer}>
          {/* Left Side: Manufacturer Info */}
          <View style={styles.headerContainer}>
            {/* ... logo, company info ... */}
            {manufacturer?.croppedLogo || manufacturer?.logoUrl ? (
              <PdfImage style={styles.logo} src={manufacturer.croppedLogo || manufacturer.logoUrl} />
            ) : (
              <Text
                style={{
                  height: styles.logo.height,
                  fontFamily: "Roboto",
                  fontWeight: 700,
                  marginBottom: styles.logo.marginBottom,
                }}>
                {t("logoMissing")}
              </Text>
            )}
            <Text style={styles.companyInfoLine}>
              {t("companyInfo", manufacturer?.company, manufacturer?.vat)}
            </Text>
            {(manufacturer?.companyPhone || manufacturer?.contactEmail) && (
              <Text style={styles.contactInfoLine}>
                {t("contactInfo", manufacturer.companyPhone, manufacturer.contactEmail)}
              </Text>
            )}
            <View style={styles.underline} />
          </View>
          {/* Right Side: Client - Only show if has values */}
          {(overriddenContactName || client?.client || overriddenContactEmail) && (
            <View style={styles.clientInfoContainer}>
              <Text style={styles.clientInfoHeader}>{t("clientInfoHeader")}</Text>
              <View style={styles.underline} />
              {overriddenContactName && (
                <Text style={styles.clientInfoDetail}>
                  {t("clientNameLabel")} {overriddenContactName}
                </Text>
              )}
              {client?.client && (
                <Text style={styles.clientInfoDetail}>
                  {t("clientCompanyLabel")} {client.client}
                </Text>
              )}
              {overriddenContactEmail && (
                <Text style={styles.clientInfoDetail}>
                  {t("clientEmailLabel")} {overriddenContactEmail}
                </Text>
              )}
            </View>
          )}
        </View>
        {/* --- End Top Section --- */}
        {/* --- Main Content Area (Two Columns) --- */}
        <View style={styles.mainContentContainer}>
          {/* --- Left Column --- */}
          <View style={styles.leftColumn}>
            {/* Title Section */}
            <View style={styles.titleSectionContainer}>
              <Text style={styles.mainTitle}>{t("proposalTitle")}</Text>
              <View style={styles.dividerLine} />
              <View style={styles.detailsContainer}>
                <View style={styles.detailLine}>
                  <Text style={styles.detailTextLabel}>{t("quotationIdLabel")} </Text>
                  <Text style={styles.detailTextValue}>{quotationId}</Text>
                </View>
                <View style={styles.detailLine}>
                  <Text style={styles.detailTextLabel}>{t("quotationDateLabel")} </Text>
                  <Text style={styles.detailTextValue}>{formattedDate}</Text>
                </View>
              </View>
            </View>

            {/* Product Section */}
            <View style={styles.productSectionContainer}>
              <Text style={styles.mainTitle}>{t("productTitle")}</Text>
              <View style={styles.dividerLine} />
              <View style={styles.detailsContainer}>
                <View style={styles.detailLine}>
                  <Text style={styles.detailTextLabel}>{t("labelsLabel")} </Text>
                  <Text style={styles.detailTextValue}>{productName}</Text>
                </View>
                <View style={styles.detailLine}>
                  <Text style={styles.detailTextLabel}>{t("sizeLabel")} </Text>
                  <Text style={styles.detailTextValue}>{sizeString}</Text>
                </View>
                <View style={styles.detailLine}>
                  <Text style={styles.detailTextLabel}>{t("materialLabel")} </Text>
                  <Text style={styles.detailTextValue}>{material}</Text>
                </View>
              </View>
            </View>

            {/* Conditional Print Section */}
            {(machineType === "Viva" || machineType === "Arsoma") && (
              <View style={styles.printSectionContainer}>
                <Text style={styles.mainTitle}>{t("printTitle")}</Text>
                <View style={styles.dividerLine} />
                <View style={styles.printDetailsGridContainer}>
                  {printData?.pantoneColors > 0 && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("pantoneLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.pantoneColors}</Text>
                    </View>
                  )}
                  {printData?.cmykColors > 0 && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("cmykLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.cmykColors}</Text>
                    </View>
                  )}
                  {printData?.varnishType && printData.varnishType !== "none" && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("varnishLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.varnishType}</Text>
                    </View>
                  )}
                  {printData?.goldOrSilver > 0 && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("goldSilverLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.goldOrSilver}</Text>
                    </View>
                  )}
                  {printData?.foilCount > 0 && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("foilCountLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.foilCount}</Text>
                    </View>
                  )}
                  {printData?.coveringWCount > 0 && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("coveringWhiteLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.coveringWCount}</Text>
                    </View>
                  )}
                  {printData?.relief && printData.relief !== "No" && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("reliefLabel")} </Text>
                      <Text style={styles.detailTextValue}>{t("reliefValueYes")}</Text>
                    </View>
                  )}
                  {printData?.foilColor && printData.foilColor !== "none" && (
                    <View style={styles.printDetailItem}>
                      <Text style={styles.detailTextLabel}>{t("foilColorLabel")} </Text>
                      <Text style={styles.detailTextValue}>{printData.foilColor}</Text>
                    </View>
                  )}
                </View>
              </View>
            )}
          </View>
          {/* --- Right Column (Image) - Only show if has image --- */}
          {productImageUrl && (
            <View style={styles.rightColumn}>
              <PdfImage style={styles.productImage} src={productImageUrl} />
            </View>
          )}
        </View>{" "}
        {/* End Main Content Area */}
        {/* --- Price Section --- */}
        <View style={styles.priceSectionContainer}>
          <Text style={styles.mainTitleFull}>{t("pricesTitle")}</Text>
          <View style={styles.dividerLineFull} />
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text
                style={[
                  styles.tableHeaderText,
                  { fontWeight: 700, width: finalDataForPdf?.showWeight ? "25%" : "33.33%", textAlign: "center" },
                ]}>
                {t("tableHeaderQty")}
              </Text>
              <Text
                style={[
                  styles.tableHeaderText,
                  { fontWeight: 700, width: finalDataForPdf?.showWeight ? "25%" : "33.33%", textAlign: "center" },
                ]}>
                {t("tableHeaderPricePer1000")}
              </Text>
              {finalDataForPdf?.showWeight && (
                <Text style={[styles.tableColWeight, styles.tableHeaderText, { fontWeight: 700, textAlign: "center" }]}>
                  {t("tableHeaderWeight")}
                </Text>
              )}
              <Text
                style={[
                  styles.tableHeaderText,
                  { fontWeight: 700, width: finalDataForPdf?.showWeight ? "25%" : "33.33%", textAlign: "center" },
                ]}>
                {t("tableHeaderTotal")}
              </Text>
            </View>
            {pricesToDisplay.length > 0 ? (
              pricesToDisplay.map((item, index) => {
                const quantity = item?.quantity || 0
                const pricePer1000 = item?.price || 0
                const totalCost = (quantity / 1000) * pricePer1000
                const labelWeight = item?.labelWeight || 0
                return (
                  <View key={`price-${index}-${quantity}`} style={styles.tableDataRow} wrap={false}>
                    <Text
                      style={[
                        styles.tableDataText,
                        { fontWeight: 700, width: finalDataForPdf?.showWeight ? "25%" : "33.33%", textAlign: "center" },
                      ]}>
                      {formatQuantity(quantity)} {t("unitPcs")}
                    </Text>
                    <Text
                      style={[
                        styles.tableDataText,
                        { fontWeight: 700, width: finalDataForPdf?.showWeight ? "25%" : "33.33%", textAlign: "center" },
                      ]}>
                      {formatCurrency(pricePer1000)} {t("unitEur")}
                    </Text>
                    {finalDataForPdf?.showWeight && (
                      <Text style={[styles.tableColWeight, styles.tableDataText, { textAlign: "center" }]}>
                        {formatWeight(labelWeight)} {t("unitKg")}
                      </Text>
                    )}
                    <Text
                      style={[
                        styles.tableDataText,
                        { width: finalDataForPdf?.showWeight ? "25%" : "33.33%", textAlign: "center" },
                      ]}>
                      {formatCurrency(totalCost)} {t("unitEur")}
                    </Text>
                  </View>
                )
              })
            ) : (
              <Text style={styles.noPricesText}>{t("noPrices")}</Text>
            )}
          </View>
        </View>
        {/* --- Conditional Startup Fees Section --- */}
        {showStartupSection && (
          <View style={styles.startupSectionContainer}>
            <Text style={styles.mainTitleFull}>{t("startupFeesTitle")}</Text>
            <View style={styles.dividerLineFull} />
            <View style={styles.costSummaryContainer}>
              {/* --- Render Plate Cost Breakdown --- */}
              {plateInfo.plateCost > 0 && plateInfo.breakdown.length > 0 && (
                <View style={styles.summaryLine}>
                  {/* Use the formatted breakdown string as the label */}
                  <Text style={styles.summaryLabel}>
                    {formatPlateBreakdown(plateInfo.breakdown)}:
                  </Text>
                  {/* Display the total plate cost as the value */}
                  <Text style={styles.summaryValue}>
                    {formatCurrency(plateInfo.plateCost)} {t("unitEur")}
                  </Text>
                </View>
              )}
              {showDieCut && (
                <View style={styles.summaryLine}>
                  <Text style={styles.summaryLabel}>{t("dieCutLabel", dieCutDims)}</Text>
                  <Text style={styles.summaryValue}>
                    {formatCurrency(dieCutCost)} {t("unitEur")}
                  </Text>
                </View>
              )}
              {(plateInfo.plateCost > 0 || (showDieCut && !showStockAvailableLine)) && ( // Show total only if there are actual costs
                <>
                  <View style={styles.summarySeparator} />
                  <View style={styles.summaryLine}>
                    <Text style={[styles.summaryLabel, styles.summaryTotalLabel]}>
                      {t("totalLabel")}
                    </Text>
                    <Text style={[styles.summaryValue, styles.summaryTotalValue]}>
                      {formatCurrency(totalStartupCost)} {t("unitEur")}
                    </Text>
                  </View>
                </>
              )}
            </View>
          </View>
        )}
        {/* --- Pricing & Terms Section --- */}
        <View
          style={styles.termsSectionContainer}
          break={pricesToDisplay.length > 5 || showStartupSection}>
          <Text style={styles.mainTitleFull}>{t("termsTitle")}</Text>
          <View style={styles.dividerLineFull} />
          <View style={styles.bulletListContainer}>
            {/* Render all terms (dynamic + static) from editingTerms or fallback */}
            {priceOfferTerms && priceOfferTerms[lang]
              ? Object.entries(priceOfferTerms[lang]).map(([termKey, termText]) => (
                  <View key={termKey} style={styles.bulletItem}>
                    <Text style={styles.bulletSymbol}>•</Text>
                    <Text style={styles.bulletText}>{termText}</Text>
                  </View>
                ))
              : null}

            {/* Fallback if no Firestore terms */}
            {!priceOfferTerms || !priceOfferTerms[lang]
              ? [
                  "termCompletion",
                  "termPaymentTerms",
                  "termPriceBase",
                  "termValidity",
                  "termPriceConditions",
                  "termToolingFirstOrder",
                  "termToolingChanges",
                  "termToolingRepeat",
                  "termCustomerSupplied",
                  "termMaterialTesting",
                  "termLiability",
                  "termAgreement",
                ].map((termKey) => (
                  <View key={termKey} style={styles.bulletItem}>
                    <Text style={styles.bulletSymbol}>•</Text>
                    <Text style={styles.bulletText}>{t(termKey)}</Text>
                  </View>
                ))
              : null}
          </View>
        </View>
      </Page>
    </Document>
  )
}

// --- Pricing & Terms Editor Component ---
function TermsEditor({ terms, selectedLang, onTermsChange }) {
  const [editingKey, setEditingKey] = useState(null)

  const currentTerms = terms?.[selectedLang] || {}
  const termKeys = Object.keys(currentTerms)

  const handleEditTerm = (key) => {
    setEditingKey(editingKey === key ? null : key)
  }

  const handleUpdateTerm = (key, value) => {
    const updatedTerms = {
      ...terms,
      [selectedLang]: {
        ...terms[selectedLang],
        [key]: value,
      },
    }
    console.log("DEBUG - TermsEditor updated term:", key, "to:", value)
    console.log("DEBUG - Updated terms object:", updatedTerms)
    onTermsChange(updatedTerms)
  }

  const handleDeleteTerm = (key) => {
    const newTerms = { ...terms[selectedLang] }
    delete newTerms[key]
    const updatedTerms = {
      ...terms,
      [selectedLang]: newTerms,
    }
    onTermsChange(updatedTerms)
  }

  const handleAddNewTerm = () => {
    const newKey = `term_${Date.now()}`
    const updatedTerms = {
      ...terms,
      [selectedLang]: {
        ...terms[selectedLang],
        [newKey]: "New term text...",
      },
    }
    onTermsChange(updatedTerms)
    setEditingKey(newKey)
  }

  return (
    <Space direction="vertical" style={{ width: "100%", height: "100%" }} size="small">
      <div style={{ flex: 1, maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
        {termKeys.map((key) => (
          <div
            key={key}
            style={{
              marginBottom: 12,
              padding: 10,
              border: "1px solid #d9d9d9",
              borderRadius: 4,
            }}>
            {editingKey === key ? (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.TextArea
                  value={terms[selectedLang][key]}
                  onChange={(e) => handleUpdateTerm(key, e.target.value)}
                  rows={3}
                  style={{ fontSize: 12 }}
                />
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={() => setEditingKey(null)}>
                    Done
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      handleDeleteTerm(key)
                      setEditingKey(null)
                    }}>
                    Delete
                  </Button>
                </Space>
              </Space>
            ) : (
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size={4}>
                <AntText
                  style={{
                    fontSize: 12,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                  {terms[selectedLang][key]}
                </AntText>
                <Space size={4}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditTerm(key)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this term?"
                    description="This action cannot be undone"
                    onConfirm={() => {
                      handleDeleteTerm(key)
                      setEditingKey(null)
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </Space>
              </Space>
            )}
          </div>
        ))}
      </div>

      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={handleAddNewTerm}
        block>
        Add Term (this PDF only)
      </Button>
      <AntText type="secondary" style={{ fontSize: 11, marginTop: 8 }}>
        Changes are temporary and only affect this PDF generation
      </AntText>
    </Space>
  )
}

// --- The Main PriceOffer Component ---
const PriceOffer = ({ data }) => {
  const [initialData, setInitialData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false) // Separate loading state for generation

  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [selectedLang, setSelectedLang] = useState("en") // Default language

  // --- State for Price Calculation & Addition ---
  const [quantityToCalculate, setQuantityToCalculate] = useState(null) // User input quantity
  const [calculationResult, setCalculationResult] = useState(null) // Result from handleCalculate { cenaPar1000uzlimemEur, tiraisSvarsKg, etc. }
  const [finalPriceToAdd, setFinalPriceToAdd] = useState(null) // The price/1000 actually used (calculated or closest)
  const [isCalculating, setIsCalculating] = useState(false) // Loading state for calculation
  const [canAddPrice, setCanAddPrice] = useState(false) // Controls enabling the final "Add" button
  // --- End Price Calculation State ---

  // Get translations for the UI (Drawer, messages etc.) based on selectedLang
  const uiT = (key, ...args) => getTranslation(selectedLang, key, ...args)

  // <<< Get Form instance >>>
  const [form] = Form.useForm()

  // State ONLY for overrides NOT managed by Form (like image)
  const [pdfOverrides, setPdfOverrides] = useState({
    productImageUrl: null,
  })

  // State for weight visibility in PDF table
  const [showWeight, setShowWeight] = useState(true)

  // State for Pricing & Terms editor
  const [priceOfferTerms, setPriceOfferTerms] = useState(null) // Original from Firestore
  const [editingTerms, setEditingTerms] = useState(null) // Local edits only for this PDF
  const [termsLoading, setTermsLoading] = useState(false)
  const [termsDrawerVisible, setTermsDrawerVisible] = useState(false) // Sub-drawer for terms

  // Fetch pricing & terms from Firestore (read-only)
  const fetchPriceOfferTerms = useCallback(async () => {
    try {
      setTermsLoading(true)
      const docRef = doc(db, "settings", "priceOfferSettings")
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        const terms = docSnap.data()
        setPriceOfferTerms(terms)
        setEditingTerms(terms) // Initialize editing terms with fetched data
      } else {
        setPriceOfferTerms(null)
        setEditingTerms(null)
      }
    } catch (err) {
      console.error("Error fetching price offer terms:", err)
      message.error("Failed to fetch pricing & terms")
    } finally {
      setTermsLoading(false)
    }
  }, [])

  // Effect to set initial data and form values
  // Effect to initialize/reset state
  useEffect(() => {
    setIsLoading(true)
    if (data) {
      setInitialData({ ...data }) // Use simple spread, consider deep clone if needed
      setPdfOverrides({ productImageUrl: data?.product?.productFileUrl || null })
      form.resetFields()
    } else {
      setInitialData(null)
      setPdfOverrides({ productImageUrl: null })
      form.resetFields()
    }
    // Reset calculation state as well
    setQuantityToCalculate(null)
    setCalculationResult(null)
    setFinalPriceToAdd(null)
    setIsCalculating(false)
    setCanAddPrice(false)
    setError(null)
    setIsLoading(false)
  }, [data, form])

  // Update dynamic terms when form values change
  const updateDynamicTerms = useCallback(
    (changedValues) => {
      if (!editingTerms) return

      const completionDays = form.getFieldValue("completionDays") || 10
      const paymentType = form.getFieldValue("paymentType") || "after"
      const paymentDays = form.getFieldValue("paymentDays") || 30

      // Generate dynamic payment term string
      const paymentTermString =
        paymentType === "pre"
          ? `Payment Terms: Pre Payment`
          : `Payment Terms: Net ${paymentDays} Days`

      // Update editingTerms with dynamic values
      setEditingTerms((prevTerms) => ({
        ...prevTerms,
        [selectedLang]: {
          ...prevTerms?.[selectedLang],
          termCompletion: `Completion: ${completionDays} Working Days`,
          termPaymentTerms: paymentTermString,
        },
      }))
    },
    [editingTerms, form, selectedLang]
  )

  // --- Drawer Handlers ---
  const showDrawer = () => {
    if (!data) return
    // Reset initialData state to discard local changes
    setInitialData({ ...data }) // Use simple spread, consider deep clone if needed

    // Reset calculation inputs/results
    setQuantityToCalculate(null)
    setCalculationResult(null)
    setFinalPriceToAdd(null)
    setIsCalculating(false)
    setCanAddPrice(false)

    // Set form defaults (image is reset here too)
    const initialProductName = data?.product?.productName || ""
    const initialImageUrl = data?.product?.productFileUrl || null
    setPdfOverrides({ productImageUrl: initialImageUrl })

    form.resetFields()
    form.setFieldsValue({
      language: selectedLang,
      contactName: "",
      contactEmail: "",
      productLabelName: initialProductName || "",
      paymentType: "after", // Default payment type on open
      paymentDays: 30, // Default payment days on open
      completionDays: 10,
    })
    setIsDrawerVisible(true)

    // Fetch pricing & terms when drawer opens
    fetchPriceOfferTerms()
  }

  // Helper to find closest price in current state (similar to LabelCount)
  const findClosestPriceInState = (targetQuantity) => {
    if (!initialData || !targetQuantity) return null
    const isFixed = initialData.advanced?.fixedPrice === true
    const priceArrayKey = isFixed ? "dynamicFixedPrice" : "dynamicPrice"
    const currentPriceArray = initialData[priceArrayKey] || []

    if (currentPriceArray.length === 0) return null // No prices to compare against

    // Add original price/labelCount if available and not already in list
    const pricesToSearch = [...currentPriceArray]
    if (
      initialData.labelCount > 0 &&
      initialData.price > 0 &&
      !isFixed &&
      !pricesToSearch.some((p) => p.quantity === initialData.labelCount)
    ) {
      pricesToSearch.push({ quantity: initialData.labelCount, price: initialData.price })
    }
    if (
      initialData.labelCount > 0 &&
      initialData.totalFixedPrice > 0 &&
      isFixed &&
      !pricesToSearch.some((p) => p.quantity === initialData.labelCount)
    ) {
      pricesToSearch.push({ quantity: initialData.labelCount, price: initialData.totalFixedPrice })
    }

    if (pricesToSearch.length === 0) return null

    let closestObj = pricesToSearch.reduce((prev, curr) => {
      return Math.abs(curr.quantity - targetQuantity) < Math.abs(prev.quantity - targetQuantity)
        ? curr
        : prev
    })
    return closestObj.price // Return the price/1000 of the closest quantity
  }

  // Function triggered by "Calculate" button
  const handleLocalPriceCalculation = async () => {
    if (!quantityToCalculate || quantityToCalculate <= 0 || !initialData) {
      message.warning("Please enter a valid quantity (> 0).")
      return
    }
    setIsCalculating(true)
    setCalculationResult(null)
    setFinalPriceToAdd(null)
    setCanAddPrice(false) // Disable Add button until calculation finishes

    try {
      console.log(
        "Calculating price for quantity:",
        quantityToCalculate,
        "using data:",
        initialData,
      )
      const result = await handleCalculate(initialData, quantityToCalculate)
      console.log("Calculation result:", result)

      // Check if result is valid (adjust based on handleCalculate's possible return values)
      if (
        !result ||
        typeof result.cenaPar1000uzlimemEur !== "number" ||
        typeof result.tiraisSvarsKg !== "number"
      ) {
        throw new Error("Calculation did not return valid price or weight.")
      }

      setCalculationResult(result) // Store the full result

      // Determine the final price/1000 to use
      if (initialData.advanced?.fixedPrice === true) {
        const closestPrice = findClosestPriceInState(quantityToCalculate)
        if (closestPrice !== null) {
          setFinalPriceToAdd(closestPrice)
          console.log("Using closest fixed price:", closestPrice)
          // Check if we have necessary data to enable Add button for fixed price
          setCanAddPrice(result.tiraisSvarsKg > 0 && result.papiraPaterins > 0) // Weight and m2 needed for fixed price entry structure?
          if (!(result.tiraisSvarsKg > 0 && result.papiraPaterins > 0)) {
            message.error("Calculation incomplete for fixed price (missing weight/m2). Cannot add.")
          }
        } else {
          message.error("Could not find a closest price for fixed price calculation.")
          setCanAddPrice(false)
        }
      } else {
        // Dynamic price: use the calculated price
        setFinalPriceToAdd(result.cenaPar1000uzlimemEur)
        console.log("Using calculated dynamic price:", result.cenaPar1000uzlimemEur)
        setCanAddPrice(true) // Enable Add button if calculation succeeded
      }
    } catch (error) {
      console.error("Price calculation failed:", error)
      message.error(`Calculation failed: ${error.message || "Unknown error"}`)
      setCalculationResult(null)
      setFinalPriceToAdd(null)
      setCanAddPrice(false)
    } finally {
      setIsCalculating(false)
    }
  }

  // Function triggered by "Add" button (after calculation)
  const handleAddCalculatedPrice = () => {
    if (
      !initialData ||
      !calculationResult ||
      finalPriceToAdd === null ||
      quantityToCalculate === null
    ) {
      message.error("Calculation results missing. Please calculate first.")
      return
    }
    if (!canAddPrice) {
      message.warning("Cannot add price. Ensure calculation was successful and conditions are met.")
      return
    }

    const isFixed = initialData.advanced?.fixedPrice === true
    const priceArrayKey = isFixed ? "dynamicFixedPrice" : "dynamicPrice"

    // Check again for duplicates before adding
    const currentPriceArray = initialData[priceArrayKey] || []
    const alreadyExists = currentPriceArray.some((p) => p.quantity === quantityToCalculate)
    if (alreadyExists) {
      message.warning(`Price for quantity ${quantityToCalculate} already exists.`)
      // return; // Prevent adding duplicate
    }

    // Create the new price entry using calculated/derived values
    const newPriceEntry = {
      quantity: quantityToCalculate, // The quantity used for calculation
      price: finalPriceToAdd, // The final price/1000 (calculated or closest)
      labelWeight: calculationResult.tiraisSvarsKg || 0, // Weight from calculation
      m2: calculationResult.papiraPaterins || 0, // m2 from calculation (if needed, like in LabelCount)
      isLocal: true,
      tempId: Date.now() + Math.random(),
    }

    // Update state immutably
    setInitialData((prevData) => {
      const updatedArray = [...(prevData[priceArrayKey] || []), newPriceEntry]
      return {
        ...prevData,
        [priceArrayKey]: updatedArray,
      }
    })

    message.success(`Local price added for quantity ${quantityToCalculate}.`)

    // Reset inputs and results for the next calculation
    setQuantityToCalculate(null)
    setCalculationResult(null)
    setFinalPriceToAdd(null)
    setCanAddPrice(false)
  }

  const handleDrawerClose = () => {
    setIsDrawerVisible(false)
    setEditingTerms(priceOfferTerms) // Discard local edits, reset to original Firestore data
    form.resetFields()
  }

  const handleLangChange = (value) => {
    setSelectedLang(value)
    // Note: We update state, UI text using uiT will update automatically
  }

  const handleImageUrlChange = (url) => {
    setPdfOverrides((prev) => ({ ...prev, productImageUrl: url }))
  }

  // Handle image selection/upload
  const handleImageUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png"
    if (!isJpgOrPng) {
      message.error(uiT("imageTypeError")) // Use translated message
      return Upload.LIST_IGNORE
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error(uiT("imageSizeError")) // Use translated message
      return Upload.LIST_IGNORE
    }
    const reader = new FileReader()
    reader.onloadend = () => handleImageUrlChange(reader.result)
    reader.onerror = (err) => {
      console.error("FileReader error:", err)
      message.error(uiT("fileReaderError")) // Use translated message
    }
    reader.readAsDataURL(file)
    return false // Prevent default upload
  }

  const handleRemoveLocalPrice = (tempIdToRemove) => {
    if (!initialData) return

    const isFixed = initialData.advanced?.fixedPrice === true
    const priceArrayKey = isFixed ? "dynamicFixedPrice" : "dynamicPrice"

    setInitialData((prevData) => {
      const currentArray = prevData[priceArrayKey] || []
      // Filter out the item to remove, ensuring it's a local one
      const updatedArray = currentArray.filter(
        (price) => !(price.isLocal && price.tempId === tempIdToRemove),
      )
      return {
        ...prevData,
        [priceArrayKey]: updatedArray,
      }
    })
    message.info("Local price removed.")
  }
  // --- End Local Price Functions ---

  // --- PDF Generation Logic (Now includes language) ---
  const generatePdfWithFinalData = useCallback(
    async (finalDataForPdf) => {
      // finalDataForPdf now includes 'language'
      setIsGenerating(true) // Use separate generation loading state
      setError(null)

      let generatedQuotationId = "" // Define here to be accessible in finally/catch if needed
      let pdfBlobUrl = null // To manage object URL lifecycle

      const currentLang = finalDataForPdf.language || "en" // Get language for errors/logs
      const genT = (key, ...args) => getTranslation(currentLang, key, ...args) // Translations for generation process

      if (!initialData?.clientId) {
        setError(`${genT("errorPrefix")} ${genT("missingInitialDataError")}`)
        setIsGenerating(false)
        return
      }
      console.log(`Starting process for Client ID: ${initialData.clientId}, Lang: ${currentLang}`)

      let stockKnifeDimensions = null

      try {
        // --- 1. Fetch Client Data ---
        console.log("Fetching client...")
        const isLead = initialData?.clientIsLead === true
        const clientCollection = isLead ? "leads" : "clients"
        const clientRef = doc(db, clientCollection, initialData.clientId)
        const clientSnap = await getDoc(clientRef)
        if (!clientSnap.exists()) throw new Error(genT("fetchClientError", initialData.clientId))
        const clientData = clientSnap.data()
        console.log(`Client data fetched from '${clientCollection}'.`)

        // --- 2. Fetch Manufacturer Data from Collection ---
        let manufacturerData = null
        const manufacturerId = clientData?.manufacturerId || clientData?.manufacturer?.id
        const clientManufacturerVat = clientData?.manufacturer?.vat

        // Try 1: Fetch by ID
        if (manufacturerId) {
          console.log(`Attempting to fetch manufacturer by ID: ${manufacturerId}`)
          const manufacturerRef = doc(db, "manufacturers", manufacturerId)
          const manufacturerSnap = await getDoc(manufacturerRef)

          if (manufacturerSnap.exists()) {
            manufacturerData = manufacturerSnap.data()
            console.log("Manufacturer data fetched from manufacturers collection by ID.")
          } else {
            console.warn(`Manufacturer ID ${manufacturerId} not found in collection. Trying VAT fallback...`)
          }
        }

        // Try 2: Fallback to VAT if ID fetch failed
        if (!manufacturerData && clientManufacturerVat) {
          console.log(`Attempting to fetch manufacturer by VAT: ${clientManufacturerVat}`)
          const manufacturersRef = collection(db, "manufacturers")
          const q = query(manufacturersRef, where("vat", "==", clientManufacturerVat))
          const querySnap = await getDocs(q)

          if (!querySnap.empty) {
            manufacturerData = querySnap.docs[0].data()
            console.log(`Manufacturer found by VAT: ${clientManufacturerVat}`)
          } else {
            console.warn(`Manufacturer with VAT ${clientManufacturerVat} not found in collection.`)
          }
        }

        // Error if neither method worked
        if (!manufacturerData) {
          throw new Error(genT("missingManufIdError"))
        }

        if (!manufacturerData?.croppedLogo && !manufacturerData?.logoUrl)
          console.warn(`${genT("warningPrefix")} ${genT("missingManufLogoWarn")}`)

        // --- 3. Get Next Quotation ID ---
        console.log("Fetching and updating quotation sequence...")
        const sequenceRef = doc(db, "sequence", "quotationSequence")
        let generatedQuotationId = ""
        await runTransaction(db, async (transaction) => {
          const sequenceSnap = await transaction.get(sequenceRef)
          if (!sequenceSnap.exists()) throw new Error(genT("sequenceDocError"))
          const sequenceData = sequenceSnap.data()
          const prefix = sequenceData.prefix || "QN"
          const lastNumber = sequenceData.lastNumber || 0
          if (typeof lastNumber !== "number") throw new Error(genT("sequenceLastError"))
          if (typeof prefix !== "string") throw new Error(genT("sequencePrefixError"))
          const nextNumber = lastNumber + 1
          generatedQuotationId = `${prefix}${String(nextNumber).padStart(4, "0")}`
          transaction.update(sequenceRef, { lastNumber: nextNumber })
          console.log(`Generated Quotation ID: ${generatedQuotationId}`)
        })
        if (!generatedQuotationId) throw new Error(genT("sequenceGenError"))

        // --- 4. Skip Stock Knife Search ---
        // Only include knife as cost if it's a new knife, no approximation/stock knife search
        stockKnifeDimensions = null

        // --- Step 5: Generate PDF ---
        console.log("Generating PDF with overrides:", finalDataForPdf)
        const blob = await pdf(
          <SimpleLogoPdf
            finalDataForPdf={finalDataForPdf} // Pass the object containing language, paymentType, paymentDays etc.
            manufacturer={manufacturerData}
            client={clientData}
            initialData={initialData} // Pass the original calculation data
            quotationId={generatedQuotationId}
            stockKnifeDimensions={stockKnifeDimensions}
            priceOfferTerms={finalDataForPdf.editingTerms} // Use explicitly passed edited terms (not stale closure)
          />,
        ).toBlob()
        console.log("PDF Blob created.")
        console.log("DEBUG - editingTerms:", editingTerms)
        console.log("DEBUG - selectedLang:", selectedLang)

        // Define file details here for reuse
        const pdfPath = `priceOffers/${generatedQuotationId}.pdf`
        const fileName = `${generatedQuotationId}.pdf`

        // --- Step 6: Upload to Firebase Storage ---
        console.log(`Uploading PDF to Firebase Storage at ${pdfPath}`)
        const fileRef = ref(storage, pdfPath)

        try {
          const uploadResult = await uploadBytes(fileRef, blob, { contentType: "application/pdf" })
          console.log(
            "PDF successfully uploaded to Firebase Storage.",
            uploadResult.metadata.fullPath,
          )

          // --- *** NEW Step 6.1: Get Download URL and Save Firestore Record *** ---
          try {
            const downloadURL = await getDownloadURL(uploadResult.ref) // Get URL after upload
            console.log("Download URL obtained:", downloadURL)

            // Prepare data for Firestore document
            const priceOfferData = {
              clientId: initialData.clientId, // Use clientId from initialData
              filePath: pdfPath, // Path in Storage
              downloadUrl: downloadURL, // Public Download URL
              fileName: fileName, // Filename
              createdAt: serverTimestamp(), // Firestore server timestamp
              productId: initialData?.id ? initialData.id : "",
            }

            // Add the document to the 'priceOffers' collection
            const priceOffersCollectionRef = collection(db, "priceOffers")
            const docRef = await addDoc(priceOffersCollectionRef, priceOfferData)
            console.log("Price offer metadata saved to Firestore with ID:", docRef.id)
          } catch (firestoreError) {
            // Log error if getting URL or saving Firestore doc fails
            console.error("Failed to get download URL or save Firestore record:", firestoreError)
            // Notify user but don't stop the rest of the process (download/open)
            message.error(`Failed to save PDF metadata record: ${firestoreError.message}`)
          }
          // --- *** END NEW Step 6.1 *** ---
        } catch (uploadError) {
          console.error("Firebase Storage upload failed:", uploadError)
          // Notify user about upload failure
          message.error(`${genT("uploadError")} ${uploadError.message}`)
          // If upload fails, we don't proceed to download/open/save-firestore in this structure
          // Re-throw the error to stop the process if upload is critical
          throw uploadError // Stop if upload fails
        }

        // --- Step 7: Trigger Browser Download ---
        console.log("Creating download link...")
        pdfBlobUrl = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = pdfBlobUrl
        link.setAttribute("download", fileName) // Use defined fileName
        document.body.appendChild(link)
        link.click()
        console.log(genT("downloadTriggered"))
        document.body.removeChild(link)

        // --- Step 8: Open PDF in New Tab ---
        window.open(pdfBlobUrl, "_blank")
        console.log("PDF opened in new tab.")

        message.success(genT("pdfSavedAndOpened"))
        setIsDrawerVisible(false)
      } catch (err) {
        // Catch errors from any step (fetch, sequence, stock, pdf gen, upload)
        console.error(
          `${genT("pdfGenError")} (During step for ID: ${generatedQuotationId || "N/A"})`,
          err,
        )
        const errorMsg = `${genT("errorPrefix")} ${err.message}`
        setError(errorMsg)
        message.error(errorMsg) // Show specific error caught
      } finally {
        setIsGenerating(false)
        // --- Step 9: Clean up Object URL ---
        if (pdfBlobUrl) {
          setTimeout(() => {
            URL.revokeObjectURL(pdfBlobUrl)
            console.log("Revoked Object URL:", pdfBlobUrl)
          }, 100)
        }
      }
    },
    [initialData], // Keep dependency
  )

  // --- <<< NEW Handler for the "Confirm" button >>> ---
  // --- Form Submission Handler ---
  const handleConfirmAndGenerate = async () => {
    try {
      const validatedValues = await form.validateFields() // Now includes paymentType and paymentDays (if applicable)
      console.log("Form Validation Success:", validatedValues)

      // Prepare the final data object, including image URL, language, and payment terms
      const finalDataForPdf = {
        language: validatedValues.language, // From form
        contactName: validatedValues.contactName, // From form
        contactEmail: validatedValues.contactEmail, // From form
        productLabelName: validatedValues.productLabelName, // From form
        productImageUrl: pdfOverrides.productImageUrl, // From state
        paymentType: validatedValues.paymentType, // <<< NEW: From form
        paymentDays: validatedValues.paymentDays, // <<< NEW: From form (will be undefined if type is 'pre')
        completionDays: validatedValues.completionDays,
        showWeight: showWeight, // <<< NEW: Include weight visibility setting
        editingTerms: editingTerms, // <<< Pass edited terms explicitly
      }

      // Trigger PDF generation with the complete data
      generatePdfWithFinalData(finalDataForPdf)
    } catch (errorInfo) {
      console.log("Form Validation Failed:", errorInfo)
      // Use the ENGLISH validation message here as drawer UI is English
      message.error("Please fill in all required fields correctly.") // Keep this generic or use specific keys if needed
    }
  }

  // --- Helper to get the current price list from state for UI display ---
  const getCurrentPricesForDisplay = () => {
    if (!initialData) return []
    const raw = initialData.advanced?.fixedPrice
      ? initialData.dynamicFixedPrice
      : initialData.dynamicPrice
    const prices = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object"
        ? Object.values(raw)
        : []
    return prices.sort((a, b) => a.quantity - b.quantity)
  }

  // Render the component UI (Using English text for UI elements)
  return (
    <div>
      {isLoading ? (
        <Spin />
      ) : (
        <>
          <Link onClick={showDrawer} disabled={!initialData || isLoading}>
            <TagsOutlined /> Price Offer
          </Link>
        </>
      )}

      <Drawer
        title="Customize PDF Details"
        width={900} // Compact drawer for 2 columns
        onClose={handleDrawerClose}
        open={isDrawerVisible}
        destroyOnClose
        footer={
          <div style={{ textAlign: "center", paddingBottom: 25 }}>
            <Space>
              <Button onClick={handleDrawerClose} disabled={isGenerating}>
                Cancel {/* English */}
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmAndGenerate}
                loading={isGenerating}
                disabled={isGenerating}>
                Confirm and Generate PDF {/* English */}
              </Button>
            </Space>
          </div>
        }>
        {isLoading ? (
          <Spin />
        ) : initialData ? (
          <Row gutter={[10, 10]}>
            {/* Left Column: Form */}
            <Col span={12}>
              <Form
                form={form}
                layout="vertical"
                name="pdfOverridesForm"
                style={{ padding: "10px 12px", width: "100%" }}
                initialValues={{ language: selectedLang }}
                onValuesChange={updateDynamicTerms}>
                {/* Language Select */}
                <Form.Item
                  label="Document Language"
                  name="language"
                  rules={[{ required: true, message: "Please select language" }]}>
                  <Select onChange={handleLangChange} placeholder="Select language">
                    <Option value="en">English</Option>
                    <Option value="lv">Latviešu</Option>
                    <Option value="ru">Русский</Option>
                  </Select>
                </Form.Item>
                {/* Contact Name, Email, Product Name */}
                <Form.Item
                  label="Client Contact Name"
                  name="contactName"
                  rules={[{ required: true, message: "Input required" }]}>
                  <Input placeholder="Juris Ozols..." />
                </Form.Item>
                <Form.Item
                  label="Client Contact Email"
                  name="contactEmail"
                  rules={[
                    { required: true, message: "Input required" },
                    { type: "email", message: "Valid email required" },
                  ]}>
                  <Input placeholder="juris@gmail.com..." type="email" />
                </Form.Item>
                <Form.Item
                  label="Product Label Name (for PDF)"
                  name="productLabelName"
                  rules={[{ required: true, message: "Input required" }]}>
                  <Input />
                </Form.Item>
                {/* ---Payment Type Select --- */}
                <Form.Item
                  label="Payment Type" // Keep label English
                  name="paymentType"
                  rules={[{ required: true, message: "Please select payment type!" }]} // Use English message
                  initialValue="after" // Default to 'After Payment'
                >
                  <Select placeholder="Select Type">
                    <Option value="pre">Pre Payment</Option> {/* Keep option text English */}
                    <Option value="after">After Payment</Option> {/* Keep option text English */}
                  </Select>
                </Form.Item>

                {/* --- Payment Days Input (Conditional) --- */}
                <Form.Item
                  noStyle // Needed for conditional requirement based on another field
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.paymentType !== currentValues.paymentType
                  }>
                  {
                    (
                      { getFieldValue }, // Function as child to check paymentType
                    ) =>
                      getFieldValue("paymentType") === "after" ? ( // Only show/require if 'after'
                        <Form.Item
                          label="Payment Days (if After Payment)" // Keep label English
                          name="paymentDays"
                          rules={[
                            {
                              required: true, // Required only when 'after' is selected
                              message: "Please enter payment days!", // English message
                            },
                            {
                              type: "number",
                              min: 1,
                              message: "Days must be greater than 0!", // English message
                            },
                          ]}
                          initialValue={30} // Default days if 'after' is selected initially
                        >
                          <InputNumber
                            min={1}
                            step={1}
                            placeholder="e.g., 14" // English placeholder
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      ) : null // Don't render the field if paymentType is not 'after'
                  }
                </Form.Item>
                {/* --- NEW: Completion Days Input --- */}
                <Form.Item
                  label="Completion Time (Working Days)" // Keep label English (using key: drawerCompletionDaysLabel)
                  name="completionDays"
                  rules={[
                    {
                      required: true,
                      message: "Please enter completion days!", // Keep message English
                    },
                    {
                      type: "number",
                      min: 1,
                      message: "Days must be greater than 0!", // Keep message English (using key: validationCompletionDays)
                    },
                  ]}
                  initialValue={10} // Default completion time
                >
                  <InputNumber
                    min={1}
                    step={1}
                    placeholder="e.g., 10" // Keep placeholder English
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                {/* --- Show Weight Checkbox --- */}
                <Form.Item>
                  <Checkbox
                    checked={showWeight}
                    onChange={(e) => setShowWeight(e.target.checked)}>
                    Show Weight in Table
                  </Checkbox>
                </Form.Item>

                {/* --- Edit Pricing & Terms Button --- */}
                <Form.Item>
                  <Button
                    type="default"
                    icon={<TagsOutlined />}
                    onClick={() => setTermsDrawerVisible(true)}
                    block>
                    Edit Pricing & Terms
                  </Button>
                </Form.Item>

              </Form>
            </Col>

            {/* Right Column: Prices */}
            <Col span={12}>
              <div style={{ padding: "10px 12px" }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Current Prices (for PDF)</Title>
                <List
                  itemLayout="horizontal" // Added for better alignment with actions
                  size="small" // Added for consistency
                  bordered // Added for clarity
                  dataSource={getCurrentPricesForDisplay()}
                  renderItem={(item) => (
                    <List.Item
                      actions={
                        // Only show the remove action for locally added items
                        item.isLocal
                          ? [
                              <Popconfirm
                                title="Remove this price?"
                                onConfirm={() => handleRemoveLocalPrice(item.tempId)} // Call handler with tempId
                                okText="Yes"
                                cancelText="No">
                                {/* Use a Button with danger style and icon */}
                                <Button
                                  type="text" // Use text button for less visual clutter in list action
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small" // Match list size
                                />
                              </Popconfirm>,
                            ]
                          : [] // No actions for original/non-local prices
                      }>
                      <List.Item.Meta
                        // Display quantity and price/1k
                        title={`Qty: ${item.quantity?.toLocaleString() ?? "N/A"}`}
                        description={
                          <span>
                            Price/1k: {item.price?.toFixed(2) ?? "N/A"} EUR
                            {/* Optionally show weight if available */}
                            {item.labelWeight > 0 && ` | Weight: ${item.labelWeight.toFixed(2)} kg`}
                            {/* Indicate if it's local */}
                            {item.isLocal && (
                              <em style={{ marginLeft: "8px", color: "#888" }}>(Local)</em>
                            )}
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                  style={{ marginBottom: "20px", maxHeight: "250px", overflowY: "auto" }} // Adjusted height slightly
                  locale={{ emptyText: "No prices available" }}
                />

                <Divider>Calculate & Add Local Price</Divider>
                {/* Calculation Section */}
                <Space direction="vertical" style={{ width: "100%" }}>
                  <InputNumber
                    placeholder="Quantity to Calculate"
                    value={quantityToCalculate}
                    onChange={setQuantityToCalculate}
                    min={1}
                    step={1000}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                    style={{ width: "100%" }}
                  />
                  <Button
                    type="dashed"
                    icon={<CalculatorOutlined />}
                    onClick={handleLocalPriceCalculation}
                    loading={isCalculating}
                    disabled={!quantityToCalculate || quantityToCalculate <= 0 || isCalculating}
                    style={{ width: "100%" }}>
                    {initialData.advanced?.fixedPrice === true
                      ? "Calculate & Find Closest Price"
                      : "Calculate Price"}
                  </Button>
                  {finalPriceToAdd !== null && (
                    <Space
                      style={{ marginTop: "10px", width: "100%", justifyContent: "space-between" }}>
                      <AntText strong>Price/1k to Add:</AntText>
                      <AntText code>{finalPriceToAdd.toFixed(2)} EUR</AntText>
                      {/* Optionally display calculated weight */}
                      {calculationResult?.tiraisSvarsKg > 0 && (
                        <AntText style={{ marginLeft: "10px" }}>
                          (Weight: {calculationResult.tiraisSvarsKg.toFixed(2)} kg)
                        </AntText>
                      )}
                    </Space>
                  )}
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCalculatedPrice}
                    disabled={!canAddPrice || isCalculating} // Disable until calc is done and valid
                    style={{ width: "100%", marginTop: "10px" }}>
                    Add Calculated Price to List
                  </Button>
                </Space>

                <Divider>Product Image (for PDF)</Divider>
                {pdfOverrides.productImageUrl ? (
                  <div
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}>
                    <AntImage
                      src={pdfOverrides.productImageUrl}
                      width={120}
                      style={{ marginBottom: 8 }}
                    />
                    <Button onClick={() => handleImageUrlChange(null)} danger size="small">
                      Remove Image
                    </Button>
                  </div>
                ) : (
                  <div
                    style={{
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    No Image
                  </div>
                )}
                <Space size="small">
                  <Upload
                    beforeUpload={handleImageUpload}
                    showUploadList={false}
                    accept="image/png, image/jpeg">
                    <Button size="small">
                      {pdfOverrides.productImageUrl ? "Change" : "Upload"}
                    </Button>
                  </Upload>
                  <AntText type="secondary" style={{ fontSize: 11 }}>Max 2MB</AntText>
                </Space>
              </div>
            </Col>
          </Row>
        ) : (
          <AntText>Loading initial data...</AntText>
        )}
      </Drawer>

      {/* Sub-Drawer: Pricing & Terms Editor */}
      <Drawer
        title="Edit Pricing & Terms"
        placement="right"
        onClose={() => setTermsDrawerVisible(false)}
        open={termsDrawerVisible}
        width={600}
        destroyOnClose>
        {termsLoading ? (
          <Spin />
        ) : !editingTerms ? (
          <AntText type="secondary">No pricing & terms found. Click Dashboard to initialize.</AntText>
        ) : (
          <TermsEditor
            terms={editingTerms}
            selectedLang={selectedLang}
            onTermsChange={setEditingTerms}
          />
        )}
      </Drawer>

      {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
    </div>
  )
}

export default PriceOffer
